import { useEffect, useState } from "react";
import { ThermoStats } from "../../types";

type StatsAction = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  className?: string;
};

type StatsTab = {
  label: string;
  stats: ThermoStats;
  action?: StatsAction;
  actions?: StatsAction[];
  disableActionsWhenInactive?: boolean;
};

type Props = {
  title: string;
  stats: ThermoStats;
  tabs?: StatsTab[];
  actionsMode?: "all" | "active";
};

export default function StatsPanel({ title, stats, tabs, actionsMode = "all" }: Props) {
  const [activeTab, setActiveTab] = useState(0);
  useEffect(() => {
    if (!tabs?.length) return;
    if (activeTab >= tabs.length) {
      setActiveTab(0);
    }
  }, [activeTab, tabs]);

  const activeStats = tabs?.length ? tabs[activeTab].stats : stats;
  const activeActions = tabs?.length
    ? tabs[activeTab].actions ?? (tabs[activeTab].action ? [tabs[activeTab].action] : [])
    : [];
  const baseActions = tabs?.length
    ? tabs.flatMap((tab) =>
        tab.actions ?? (tab.action ? [tab.action] : [])
      )
    : [];
  const activeActionLabels = new Set(
    tabs?.length
      ? (tabs[activeTab].actions ?? (tabs[activeTab].action ? [tabs[activeTab].action] : [])).map(
          (action) => action.label
        )
      : []
  );
  const disableWhenInactive = tabs?.length
    ? tabs.some((tab) => tab.disableActionsWhenInactive)
    : false;
  const actionsToRender = (actionsMode === "active" ? activeActions : baseActions).map(
    (action) => ({
      ...action,
      disabled:
        action.disabled ||
        (actionsMode === "all" && disableWhenInactive && !activeActionLabels.has(action.label))
    })
  );
  const compactButtonStyle = {
    padding: "8px 12px",
    fontSize: 13,
    borderRadius: 10,
    lineHeight: 1.2
  } as const;

  const rows = [
    { label: "Min Temp", value: `${activeStats.minTemperature.toFixed(2)} °C` },
    { label: "Mean Temp", value: `${activeStats.meanTemperature.toFixed(2)} °C` },
    { label: "Max Temp", value: `${activeStats.maxTemperature.toFixed(2)} °C` },
    { label: "Min Viscosity", value: `${activeStats.minViscosity.toFixed(4)} log10(η / dPa·s)` },
    { label: "Mean Viscosity", value: `${activeStats.meanViscosity.toFixed(4)} log10(η / dPa·s)` },
    { label: "Max Viscosity", value: `${activeStats.maxViscosity.toFixed(4)} log10(η / dPa·s)` }
  ];
  return (
    <div className="card">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 8
        }}
      >
        <h4 className="section-title" style={{ fontSize: 20, marginBottom: 0 }}>
          {title}
        </h4>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {tabs?.length ? (
            <div
              role="tablist"
              aria-label={`${title} tabs`}
              style={{ display: "flex", gap: 6, flexWrap: "wrap" }}
            >
              {tabs.map((tab, index) => {
                const isActive = index === activeTab;
                return (
                  <button
                    key={tab.label}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className="button secondary"
                    onClick={() => setActiveTab(index)}
                    style={
                      isActive
                        ? {
                            borderColor: "#6be7c5",
                            color: "#ecf1ff",
                            background: "rgba(107, 231, 197, 0.12)",
                            ...compactButtonStyle
                          }
                        : compactButtonStyle
                    }
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          ) : null}
          {actionsToRender.length
            ? actionsToRender.map((action, index) => (
                <button
                  key={`${action.label}-${index}`}
                  type="button"
                  className={
                    action.className ? `button secondary ${action.className}` : "button secondary"
                  }
                  onClick={action.onClick}
                  disabled={action.disabled}
                  title={action.title}
                  style={compactButtonStyle}
                >
                  {action.label}
                </button>
              ))
            : null}
        </div>
      </div>
      <div className="stats-rows">
        {rows.map((row) => (
          <div key={row.label} className="stats-row">
            <span className="stats-row-label">{row.label}</span>
            <strong className="stats-row-value">{row.value}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}
